import { useMemo } from 'react'

import { travelDayLabel } from '../../lib/calendar'
import { activeStages, emptyDiary, totalSpent } from '../../lib/trip'
import { erstesVorkommen } from '../../lib/tulip'
import { fahrzeug } from '../../lib/vehicle'
import type { Checks, DiaryEntry, DiaryPost, Expense, PhotoMeta, Trip } from '../../lib/types'
import { BackPage, BudgetPage, ChecklistPage, LegendPage, NotesPage } from './BookBack'
import { MetricsPage, OverviewPage, TitlePage } from './BookFront'
import { titlePhoto } from './imageFit'
import { printMetrics } from './metrics'
import { bookPages, NOTIZSEITEN, UEBERSICHT_KEY } from './sections'
import { StagePage } from './StagePage'
import { druckBeitraege, planStage, type StagePlan } from './stagePlan'

export interface PrintBookProps {
  trip: Trip
  diary: Record<string, DiaryEntry>
  /** Die Tagebuchbeiträge je Etappen-ID, ältester zuerst. */
  postsByStage: Record<string, DiaryPost[]>
  expenses: Expense[]
  checks: Checks
  photosByStage: Record<string, PhotoMeta[]>
  /** Objekt-URLs der Fotos, nach Foto-ID. */
  photoUrls: Record<string, string>
  /** Objekt-URLs der Kartenausschnitte, nach Etappen-ID und UEBERSICHT_KEY. */
  mapUrls: Record<string, string>
  /** Datum auf der Rückseite, als ISO-Datum. */
  gedrucktAm: string
}

/**
 * Das ganze Buch, Seite für Seite.
 *
 * Die Reihenfolge kommt aus bookPages, die Seitenzahlen ebenfalls - diese
 * Komponente entscheidet nichts, sie setzt nur. Das ist Absicht: was ins Buch
 * kommt und in welcher Folge, ist eine Frage mit einer richtigen Antwort und
 * gehört in eine geprüftte Funktion, nicht in eine Reihe von JSX-Ausdrücken,
 * bei denen man das Verschieben einer Zeile nicht bemerkt.
 *
 * Gerendert wird immer alles, auch wenn Karten oder Fotos noch fehlen. Ein Buch,
 * das erst nach dem Rendern der Karten sichtbar wird, wäre im Reiter minutenlang
 * eine leere Fläche; und ohne Karten ist es nicht kaputt, sondern hat Skizzen.
 */
export function PrintBook(props: PrintBookProps) {
  const { trip, diary, photosByStage } = props

  const stages = useMemo(() => activeStages(trip), [trip])

  const metrics = useMemo(
    () =>
      printMetrics({
        trip,
        diary,
        expenses: props.expenses,
        photosByStage,
      }),
    [trip, diary, props.expenses, photosByStage],
  )

  // Ein Plan je Etappe: welche Vorlage, wie viel Text auf welche Seite, welches
  // Foto auf welchen Platz.
  const plans = useMemo(() => {
    const out: Record<string, StagePlan> = {}
    for (const stage of stages) {
      out[stage.id] = planStage({
        // Die Entscheidung über den Vorspann fällt hier und nicht in der
        // Seitenplanung: sie hängt an den Beiträgen der Etappe und muss
        // dieselbe sein, mit der gerechnet und mit der gesetzt wird.
        beitraege: druckBeitraege(props.postsByStage[stage.id] ?? []),
        photoCount: photosByStage[stage.id]?.length ?? 0,
        tulipCount: stage.tulips?.length ?? 0,
        // Kopfzeile und Planungsspalten gehen dem Tagebuchtext von der Seite ab.
        // Die Planung bekommt sie deshalb mit und schätzt sie nicht.
        from: stage.from,
        to: stage.to,
        planung: {
          highlights: stage.highlights,
          night: stage.night,
          camping: stage.camping,
          tip: stage.tip,
        },
      })
    }
    return out
  }, [stages, props.postsByStage, photosByStage])

  /*
   * Wo eine Form zum ersten Mal im Buch steht - dort und nur dort steht ihr
   * Name klein neben dem Zeichen. Gerechnet über alle Etappen zusammen, denn
   * eine Etappenseite weiss nicht, was auf den Seiten davor stand.
   */
  const erstmals = useMemo(() => erstesVorkommen(stages.map((stage) => stage.tulips)), [stages])

  const pages = useMemo(
    () =>
      bookPages({
        stages,
        continuations: Object.fromEntries(
          Object.entries(plans).map(([id, plan]) => [id, plan.continuations.length]),
        ),
        notes: NOTIZSEITEN,
        hasBudget: props.expenses.length > 0 && totalSpent(props.expenses) > 0,
        hasTulips: stages.some((stage) => (stage.tulips?.length ?? 0) > 0),
        hasVignetten: props.checks.vignettes.length > 0,
        hasKit: props.checks.kit.length > 0,
      }),
    [stages, plans, props.expenses, props.checks],
  )

  const titel = useMemo(() => {
    const alle = stages.flatMap((stage) => photosByStage[stage.id] ?? [])
    return titlePhoto(alle)
  }, [stages, photosByStage])

  const fuss = trip.meta.title

  return (
    <div className="dr-buch">
      {pages.map((seite) => {
        switch (seite.kind) {
          case 'titel':
            return (
              <TitlePage
                key={seite.key}
                trip={trip}
                metrics={metrics}
                photoUrl={titel ? props.photoUrls[titel.id] : undefined}
              />
            )

          case 'uebersicht':
            return (
              <OverviewPage
                key={seite.key}
                stages={stages}
                diary={diary}
                page={seite.page}
                fuss={fuss}
                mapUrl={props.mapUrls[UEBERSICHT_KEY]}
              />
            )

          case 'kennzahlen':
            return (
              <MetricsPage
                key={seite.key}
                trip={trip}
                metrics={metrics}
                page={seite.page}
                fuss={fuss}
              />
            )

          case 'etappe': {
            const nr = stages.findIndex((kandidat) => kandidat.id === seite.stageId)
            const stage = stages[nr]
            const plan = seite.stageId ? plans[seite.stageId] : undefined
            if (!stage || !plan) return null
            return (
              <StagePage
                key={seite.key}
                stage={stage}
                entry={diary[stage.id] ?? emptyDiary(stage.id)}
                photos={photosByStage[stage.id] ?? []}
                plan={plan}
                // Der Reisetag kommt aus trip.stages und nicht aus der Stelle
                // in stages: beide Listen sind hier zwar deckungsgleich, aber
                // die Zahl auf dem Papier soll aus derselben Rechnung kommen
                // wie der Zeitraum auf dem Umschlag.
                tag={travelDayLabel(trip.stages, stage)}
                part={seite.part ?? 1}
                page={seite.page}
                fuss={fuss}
                mapUrl={props.mapUrls[stage.id]}
                photoUrls={props.photoUrls}
                erstmals={erstmals[nr]}
              />
            )
          }

          case 'budget':
            return (
              <BudgetPage
                key={seite.key}
                expenses={props.expenses}
                metrics={metrics}
                currency={trip.meta.currency}
                fuel={fahrzeug(trip).fuel}
                page={seite.page}
                fuss={fuss}
              />
            )

          case 'vignetten':
          case 'ausruestung':
            return (
              <ChecklistPage
                key={seite.key}
                checks={props.checks}
                kind={seite.kind}
                page={seite.page}
                fuss={fuss}
              />
            )

          case 'notizen':
            return <NotesPage key={seite.key} page={seite.page} fuss={fuss} />

          case 'legende':
            return (
              <LegendPage key={seite.key} part={seite.part ?? 1} page={seite.page} fuss={fuss} />
            )

          case 'rueckseite':
            return (
              <BackPage
                key={seite.key}
                trip={trip}
                gedrucktAm={props.gedrucktAm}
                mitWetter={Object.values(diary).some((e) => e.weatherFromTrack)}
              />
            )
        }
      })}
    </div>
  )
}
